import React, { FormEvent, KeyboardEvent, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  MenuItem,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CircularProgress from "@mui/material/CircularProgress";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BoltIcon from "@mui/icons-material/Bolt";
import PersonIcon from "@mui/icons-material/Person";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BuildIcon from "@mui/icons-material/Build";
import HomeIcon from "@mui/icons-material/Home";
import GroupIcon from "@mui/icons-material/Group";

import {
  FormData,
  ReportData,
  calculateReport,
  typePresets,
} from "./calculateReport";
import { RootState, useAppDispatch, useAppSelector } from "../../stores";
import {
  getUserSpecificChargingOutput,
  createUserChargingOutput,
} from "../../features/charging-output-module/chargingOutputAction";
import {
  savedGeneratedReport,
  getUtilityByZipCode,
  getScheduleByUtility,
  getEnergyBySchedule,
  getScheduleDetailTip,
  getRewiringAmericaIncentives,
  Schedule,
  RewiringAmericaIncentivesResponse,
  GrantCostDetail,
} from "../../features/generate-report-module/reportAction";
import { resetReportState } from "../../features/generate-report-module/reportSlice";
import { ViewGeneratedReportState } from "./ViewGeneratedReport";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.95)",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  background: "#fff",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
    minHeight: "100vh",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    justifyContent: "flex-start",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(1),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(-1),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3f51b5",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3f51b5",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const DropdownButton = styled(Button)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  margin: "0 auto",
  textTransform: "none",
  fontWeight: 600,
  backgroundColor: "#4b5b5d",
  color: "#fff",
  borderRadius: "6px",
  padding: theme.spacing(0.7),
  fontSize: "0.65rem",
  "&:hover": {
    backgroundColor: "#354142",
  },
  "&:disabled": {
    backgroundColor: "#bdbdbd",
    color: "#fff",
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "28px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const SubHeading = styled(Typography)(({ theme }) => ({
  color: "#4b5b5d",
  fontSize: 22,
  fontWeight: 600,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
    marginBottom: theme.spacing(1),
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#455354",
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #455354)",
  color: "#fff",
  borderRadius: "8px",
  padding: theme.spacing(1, 5),
  fontSize: "16px",
  "&:hover": {
    background: "#354142",
  },
  "&:disabled": {
    background: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.25),
    fontSize: "14px",
    minHeight: "48px",
  },
}));

const FreeChargingText = styled(Typography)(({ theme }) => ({
  color: "#2e7d32",
  fontWeight: "bold",
  fontSize: "0.975rem",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
  },
}));

const CustomInput = styled(TextField)(({ theme }) => ({
  margin: theme.spacing(0.5),
  "& .MuiOutlinedInput-root": {
    borderRadius: "3px",
    backgroundColor: "#fafafa",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3f51b5",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    fontSize: "0.875rem",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3f51b5",
  },
  "& .MuiOutlinedInput-input": {
    padding: "7px 10px",
    fontSize: "0.75rem",
  },
  "& input": {
    autocomplete: "off",
  },
}));

const CustomMenuProps = {
  PaperProps: {
    style: {
      maxHeight: 250,
      maxWidth: 220,
      overflowY: "auto" as const,
      backgroundColor: "#fff",
      marginTop: 5,
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
      borderRadius: "7px",
      border: "1px solid #e0e0e0",
    },
  },
};

interface GenerateReportsProps {
  onSubmit?: (state: ViewGeneratedReportState) => void;
}

const GenerateReports: React.FC<GenerateReportsProps> = ({ onSubmit }) => {
  const initialFormData: FormData = {
    address: "",
    city: "",
    zipCode: "",
    dwell: "60",
    dwellCategory: "Shopping Centers / Retail",
    chargers: "4",
    evs: "medium",
    type: "6.7-7.7-650-1500",
    hardware: "2600",
    install: "6000",
    pricingType: "kwh",
    pricePerKwh: "0.18",
    pricePerMin: "5",
    pricePerHour: "2",
    flatSessionFee: "2",
    idleFee: "5",
    gracePeriod: "10",
    maintenance: "300",
    networkPerPort: "0",
    grant: "0",
    firstName: "",
    lastName: "",
    email: "",
    clientId: "",
    ownerStatus: "homeowner",
    householdIncome: "",
    householdSize: "1",

    // Tax benefits defaults
    applyTaxBenefits: false,
    businessTaxRate: "21",
    section179Limit: "2500000",
    bonusDepreciationPct: "0",

    // Profit sharing defaults
    customerSplit: "100",
    softwareProviderSplit: "0",
    operatorSplit: "0",
  };

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Redux state from chargingOutput and report slices
  const { chargingOutputs } = useAppSelector(
    (state: RootState) => state.chargingOutput
  );
  const {
    ratekWh,
    utilityLoading,
    scheduleLoading,
    utilityError,
    scheduleError,
    energyError,
    utilityData,
    scheduleData,
    scheduleDetailData,
    scheduleDetailLoading,
    scheduleDetailError,
    energyLoading,
    incentivesData,
    incentivesLoading,
    incentivesError,
  } = useAppSelector((state: RootState) => state.report);

  const utilityId = utilityData?.UtilityID || "";

  // Initialize formData
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [chargingOutput, setChargingOutput] = useState<Record<string, string>>({
    "6.7-7.7-650-1500": "6.7-7.7 kW",
    "8.3-9.6-800-1600": "8.3-9.6 kW",
    "10.0-11.5-900-1700": "10.0-11.5 kW",
    "16.6-19.2-1200-2000": "16.6-19.2 kW",
  });
  const [chargingOutputOrder, setChargingOutputOrder] = useState<string[]>([
    "6.7-7.7-650-1500",
    "8.3-9.6-800-1600",
    "10.0-11.5-900-1700",
    "16.6-19.2-1200-2000",
  ]);
  const [presets, setPresets] = useState(typePresets);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [minChargingOutput, setMinChargingOutput] = useState("");
  const [maxChargingOutput, setMaxChargingOutput] = useState("");
  const [hardwarePerInput, setHardwarePerInput] = useState("");
  const [installPerInput, setInstallPerInput] = useState("");
  const [isZipApiComplete, setIsZipApiComplete] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(
    null
  );
  const [excludeIncentives, setExcludeIncentives] = useState(false);

  // Helper to check if ZIP code is valid (5 digits, no empty)
  const hasValidZip = formData.zipCode && /^\d{5}$/.test(formData.zipCode);

  // Helper to check if incentives fields are valid
  const hasValidIncentivesFields =
    formData.ownerStatus &&
    formData.householdIncome &&
    parseFloat(formData.householdIncome) >= 0 &&
    formData.householdSize;

  // Use dynamic utilityRate based on ratekWh from API (converted to dollars per kWh)
  const utilityRate = ratekWh ? parseFloat(ratekWh) : 0.18;

  // Reset all states and Redux store on component mount
  useEffect(() => {
    setFormData(initialFormData);
    setTouched({});
    setErrors({});
    setLoading(false);
    setMinChargingOutput("");
    setMaxChargingOutput("");
    setHardwarePerInput("");
    setInstallPerInput("");
    setIsZipApiComplete(false);
    setSelectedSchedule(null);
    dispatch(resetReportState());
    dispatch(getUserSpecificChargingOutput({ limit: 100 }));
  }, [dispatch]);

  // Fetch utility ID when zip code changes and is valid
  useEffect(() => {
    if (hasValidZip) {
      const timer = setTimeout(() => {
        if (hasValidZip) {
          dispatch(getUtilityByZipCode(formData.zipCode));
        }
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setIsZipApiComplete(false);
    }
  }, [formData.zipCode, dispatch, hasValidZip]);

  // Fetch schedule when utility ID is available (only if valid ZIP)
  useEffect(() => {
    if (hasValidZip && utilityId && !utilityError) {
      dispatch(getScheduleByUtility(utilityId));
    }
  }, [utilityId, utilityError, dispatch, hasValidZip]);

  // Find valid schedule with positive ratekWh
  useEffect(() => {
    const findValidSchedule = async () => {
      if (
        !hasValidZip ||
        !scheduleData ||
        scheduleData.length === 0 ||
        scheduleError
      )
        return;
      for (const sched of scheduleData) {
        try {
          const energyRes = await dispatch(
            getEnergyBySchedule(sched.ScheduleID)
          ).unwrap();
          if (
            energyRes.success &&
            energyRes.data?.ratekWh &&
            parseFloat(energyRes.data.ratekWh) > 0
          ) {
            setSelectedSchedule(sched);
            dispatch(getScheduleDetailTip(sched.ScheduleID));
            return;
          }
        } catch (err) {
          console.error(`Error for schedule ${sched.ScheduleID}:`, err);
        }
      }
      // If none found
      setSelectedSchedule(null);
      dispatch({
        type: "report/getEnergyBySchedule/rejected",
        payload: "No valid rate found",
      });
    };
    findValidSchedule();
  }, [scheduleData, scheduleError, dispatch, hasValidZip]);

  // Fetch incentives when ZIP code and incentives fields are valid
  useEffect(() => {
    if (hasValidZip && hasValidIncentivesFields) {
      const timer = setTimeout(() => {
        if (
          formData.ownerStatus &&
          formData.householdIncome &&
          formData.householdSize &&
          formData.zipCode
        ) {
          dispatch(
            getRewiringAmericaIncentives({
              owner_status: formData.ownerStatus,
              household_income: parseFloat(formData.householdIncome),
              household_size: parseInt(formData.householdSize),
              zip: formData.zipCode,
            })
          );
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [
    formData.zipCode,
    formData.ownerStatus,
    formData.householdIncome,
    formData.householdSize,
    dispatch,
    hasValidZip,
    hasValidIncentivesFields,
  ]);

  useEffect(() => {
    if (hasValidZip) {
      if (
        !utilityLoading &&
        !scheduleLoading &&
        !energyLoading &&
        !scheduleDetailLoading &&
        !incentivesLoading &&
        (ratekWh ||
          utilityError ||
          scheduleError ||
          energyError ||
          scheduleDetailError ||
          incentivesError)
      ) {
        setIsZipApiComplete(true);
      } else {
        setIsZipApiComplete(false);
      }
    } else {
      setIsZipApiComplete(false);
    }
  }, [
    hasValidZip,
    utilityLoading,
    scheduleLoading,
    energyLoading,
    scheduleDetailLoading,
    incentivesLoading,
    ratekWh,
    utilityError,
    scheduleError,
    energyError,
    scheduleDetailError,
    incentivesError,
  ]);

  /**
   * Pricing auto defaults based on utility rate
   * Guardrail for Price per kWh:
   *   If fetched rate is missing or ≤ 0.05 dollars use 0.18 dollars instead
   *   No markup on Price per kWh
   * Other pricing types (minute, hour, session) still use markup
   */
  useEffect(() => {
    // Cannot compute anything if we do not have a rate or data is loading
    if (
      !ratekWh ||
      utilityLoading ||
      scheduleLoading ||
      energyLoading ||
      scheduleDetailLoading
    ) {
      return;
    }

    const whpm = typePresets[formData.type]?.whpm || 7.2;
    const dwell = parseFloat(formData.dwell) || 60;
    const markup = 1.5; // keep markup for minute, hour, session etc

    const T_idle = formData.gracePeriod
      ? parseFloat(formData.gracePeriod)
      : 10;

    const R_kWh = utilityRate; // raw utility rate from API (or 0.18 fallback if missing)
    const kWhPerMin = whpm / 60;
    const costPerMinDollar = R_kWh * kWhPerMin;

    const E = (dwell / 60) * whpm;
    const newFormData: Partial<FormData> = {};

    // Guardrail for Price per kWh only:
    // if the fetched rate is missing or ≤ 0.05 dollars, use 0.18 dollars as base
    const rawRate = ratekWh ? parseFloat(ratekWh) : NaN;
    const pricePerKwhBase =
      !Number.isNaN(rawRate) && rawRate > 0.07 ? rawRate : 0.18;

    switch (formData.pricingType) {
      case "kwh": {
        // Only apply guardrail and no markup here
        if (!touched.pricePerKwh) {
          newFormData.pricePerKwh = pricePerKwhBase.toFixed(2);
        }
        break;
      }

      case "minute": {
        if (!touched.pricePerMin) {
          newFormData.pricePerMin = (costPerMinDollar * 100 * markup).toFixed(
            2
          );
        }
        break;
      }

      case "hour": {
        if (!touched.pricePerHour) {
          newFormData.pricePerHour = (R_kWh * whpm * markup).toFixed(2);
        }
        break;
      }

      case "session": {
        if (!touched.flatSessionFee) {
          newFormData.flatSessionFee = (E * R_kWh * markup).toFixed(2);
        }
        break;
      }

      case "minute_idle": {
        if (!touched.pricePerMin) {
          newFormData.pricePerMin = (costPerMinDollar * 100 * markup).toFixed(
            2
          );
        }
        if (!touched.idleFee) {
          newFormData.idleFee = (0.4 * T_idle).toFixed(2);
        }
        if (!touched.gracePeriod) {
          newFormData.gracePeriod = "10";
        }
        break;
      }

      case "kwh_idle": {
        // Same guardrail as kwh, no markup
        if (!touched.pricePerKwh) {
          newFormData.pricePerKwh = pricePerKwhBase.toFixed(2);
        }
        if (!touched.idleFee) {
          newFormData.idleFee = (0.4 * T_idle).toFixed(2);
        }
        if (!touched.gracePeriod) {
          newFormData.gracePeriod = "10";
        }
        break;
      }

      case "free": {
        // Nothing to auto fill
        break;
      }
    }

    if (Object.keys(newFormData).length > 0) {
      setFormData(prev => ({ ...prev, ...newFormData }));
    }
  }, [
    ratekWh,
    utilityRate,
    formData.pricingType,
    formData.dwell,
    formData.type,
    formData.gracePeriod,
    touched,
    utilityLoading,
    scheduleLoading,
    energyLoading,
    scheduleDetailLoading,
  ]);

  useEffect(() => {
    const staticChargingOutput: Record<string, string> = {
      "6.7-7.7-650-1500": "6.7–7.7 kW",
      "8.3-9.6-800-1600": "8.3–9.6 kW",
      "10.0-11.5-900-1700": "10.0–11.5 kW",
      "16.6-19.2-1200-2000": "16.6–19.2 kW",
    };
    const staticOrder = [
      "6.7-7.7-650-1500",
      "8.3-9.6-800-1600",
      "10.0-11.5-900-1700",
      "16.6-19.2-1200-2000",
    ];
    const combinedChargingOutput = { ...staticChargingOutput };
    const combinedPresets = { ...typePresets };
    const combinedOrder: string[] = [...staticOrder];
    chargingOutputs.forEach(
      (output: {
        minkW: number;
        maxkW: number;
        hardwareCost: number;
        installCost: number;
      }) => {
        const minStr = output.minkW.toFixed(1);
        const maxStr = output.maxkW.toFixed(1);
        const hardwareStr = output.hardwareCost.toString();
        const installStr = output.installCost.toString();
        const key = `${minStr}-${maxStr}-${hardwareStr}-${installStr}`;
        const displayValue = `${minStr}–${maxStr} kW`;
        if (!combinedChargingOutput[key]) {
          combinedChargingOutput[key] = displayValue;
          combinedOrder.unshift(key);
          combinedPresets[key] = {
            whpm: output.maxkW,
            hardware: output.hardwareCost,
            install: output.installCost,
          };
        }
      }
    );
    setChargingOutput(combinedChargingOutput);
    setChargingOutputOrder(combinedOrder);
    setPresets(combinedPresets);
  }, [chargingOutputs]);

  useEffect(() => {
    const chargersNum = parseFloat(formData.chargers) || 1;
    const preset = presets[formData.type];
    if (preset) {
      setFormData(prev => ({
        ...prev,
        hardware: (preset.hardware * chargersNum).toFixed(0),
        install: (preset.install * chargersNum).toFixed(0),
      }));
    } else {
      console.warn(
        `Preset not found for type: ${formData.type}, using current values`
      );
    }
  }, [formData.type, formData.chargers, presets]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDZsmG9zzmlEZLY3BpPYeJMfUphIobw-sM&libraries=places`;
    script.async = true;
    document.body.appendChild(script);
    script.onload = () => {
      const addressInput = document.getElementById(
        "address-input"
      ) as HTMLInputElement;

      if (addressInput) {
        const addressAutocomplete = new (window as any).google.maps.places
          .Autocomplete(addressInput, {
            types: ["address"],
            componentRestrictions: { country: "us" },
            fields: ["address_components", "geometry", "formatted_address"],
          });
        addressAutocomplete.addListener("place_changed", () => {
          const place = addressAutocomplete.getPlace();
          const components = place.address_components || [];
          let city = "";
          let zipCode = "";
          components.forEach((cmp: any) => {
            if (cmp.types.includes("locality")) city = cmp.long_name;
            if (cmp.types.includes("postal_code")) zipCode = cmp.long_name;
          });
          setFormData(prev => ({
            ...prev,
            address: place.formatted_address || prev.address,
            city: city || prev.city,
            zipCode: zipCode || prev.zipCode,
          }));
          setTouched(prev => ({
            ...prev,
            address: true,
            city: true,
            zipCode: true,
          }));
        });
      }

      const zipInput = document.getElementById(
        "zipcode-input"
      ) as HTMLInputElement;
      if (zipInput) {
        const zipAutocomplete = new (window as any).google.maps.places
          .Autocomplete(zipInput, {
            types: ["(regions)"],
            componentRestrictions: { country: "us" },
            fields: ["address_components", "geometry", "formatted_address"],
          });
        zipAutocomplete.addListener("place_changed", () => {
          const place = zipAutocomplete.getPlace();
          const components = place.address_components || [];
          let city = "";
          let address = "";
          let zipCode = "";
          components.forEach((cmp: any) => {
            if (cmp.types.includes("locality")) city = cmp.long_name;
            if (cmp.types.includes("postal_code")) zipCode = cmp.long_name;
          });
          address = place.formatted_address || city;
          setFormData(prev => ({
            ...prev,
            address: address || prev.address,
            city: city || prev.city,
            zipCode: zipCode || prev.zipCode,
          }));
          setTouched(prev => ({
            ...prev,
            address: true,
            city: true,
            zipCode: true,
          }));
        });
      }
    };
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Function to process incentives
  const processGrantCostDetails = (
    incentivesData: RewiringAmericaIncentivesResponse["data"]
  ): GrantCostDetail[] => {
    if (!incentivesData || !incentivesData.incentives) {
      return [];
    }

    return incentivesData.incentives.map(incentive => {
      // Format credit amount for display
      let creditAmount = "";
      let fullAmountDetails = "";
      if (incentive.amount.type === "dollar_amount") {
        creditAmount = `$${incentive.amount.number.toLocaleString()}`;
        fullAmountDetails = `Type: Dollar Amount\nAmount: $${incentive.amount.number.toLocaleString()}`;
      } else if (incentive.amount.type === "percent") {
        creditAmount = `${incentive.amount.number}%`;
        fullAmountDetails = `Type: Percentage\nRate: ${incentive.amount.number}%`;
        if (incentive.amount.representative) {
          creditAmount += ` (approx. $${incentive.amount.representative.toLocaleString()})`;
          fullAmountDetails += `\nRepresentative Amount: $${incentive.amount.representative.toLocaleString()}`;
        }
      } else {
        creditAmount = "Variable";
        fullAmountDetails = `Type: ${
          incentive.amount.type
        }\nDetails: ${JSON.stringify(incentive.amount, null, 2)}`;
      }

      // Add all amount properties to the tooltip
      Object.entries(incentive.amount).forEach(([key, value]) => {
        if (key !== "type" && key !== "number" && key !== "representative") {
          fullAmountDetails += `\n${key}: ${value}`;
        }
      });

      // Format applies to (take first item and format it)
      const appliesTo =
        incentive.items && incentive.items.length > 0
          ? incentive.items[0]
              .split("_")
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ")
          : "Not specified";

      // Format eligibility
      const eligibility =
        incentive.owner_status && incentive.owner_status.length > 0
          ? incentive.owner_status
              .map(status => status.charAt(0).toUpperCase() + status.slice(1))
              .join(", ")
          : "Not specified";

      // Format valid until
      const validUntil = incentive.end_date || "-";
      return {
        programName: incentive.program,
        creditAmount,
        fullAmountDetails,
        appliesTo,
        eligibility,
        validUntil,
        actionLink: incentive.program_url,
        description: incentive.short_description,
      };
    });
  };

  const handleChange = (e: any, child?: React.ReactNode) => {
    const name = e.target.name;
    let value = e.target.value;

    // Validation for specific fields
    if (
      [
        "zipCode",
        "minChargingOutput",
        "maxChargingOutput",
        "hardwarePerInput",
        "installPerInput",
        "householdIncome",
      ].includes(name) &&
      value.includes(" ")
    ) {
      let fieldLabel = "";
      switch (name) {
        case "zipCode":
          fieldLabel = "Zip Code";
          break;
        case "minChargingOutput":
          fieldLabel = "Minimum Charging Output";
          break;
        case "maxChargingOutput":
          fieldLabel = "Maximum Charging Output";
          break;
        case "hardwarePerInput":
          fieldLabel = "Hardware Cost";
          break;
        case "installPerInput":
          fieldLabel = "Install Cost";
          break;
        case "householdIncome":
          fieldLabel = "Household Income";
          break;
      }
      toast.error(`${fieldLabel} cannot contain spaces`);
      return;
    }

    if (name === "zipCode") {
      if (!/^\d*$/.test(value)) {
        toast.error("Zip Code can only contain numbers (0-9)");
        return;
      }
      if (value.length > 5) {
        toast.error("Zip Code must be exactly 5 digits");
        return;
      }
      if (value.length <= 5) {
        setErrors(prev => ({
          ...prev,
          zipCode: "",
        }));
      }
    }

    if (name === "householdIncome" && value && !/^\d*\.?\d*$/.test(value)) {
      toast.error("Household Income must be a valid number");
      return;
    }

    if (e.target.type === "number" && parseFloat(value) < 0) {
      toast.error(
        `${e.target.labels?.[0]?.textContent || "Field"} cannot be negative`
      );
      return;
    }

    // Handle dwell and evs changes
    let newFormData: Partial<FormData> = { [name]: value };
    if (name === "dwell") {
      const category = child ? (child as any).props["data-category"] || "" : "";
      newFormData.dwellCategory = category;
    } else if (name === "evs") {
      // Update dwell based on evs level and current dwellCategory
      const category = formData.dwellCategory || "Shopping Centers / Retail";
      const dwellValues: Record<string, string> = {
        "Shopping Centers / Retail": "60",
        "Apartment Complex / Multi-family": "0",
        "Hotels / Lodging": "600",
        "Office Buildings": "480",
        "Restaurants / Cafés": "90",
        "Theaters / Entertainment": "150",
        "Gyms / Fitness": "90",
        "Hospitals / Medical": "0",
        "Universities / Campus": "0",
        "Parking Garages / Lots": "1380",
        "Household / Home": "720",
        "Grocery Stores / Supermarkets": "45",
      };
      newFormData.dwell = dwellValues[category] || "60";
    }

    setFormData(prev => ({
      ...prev,
      ...newFormData,
    }));

    // Validate fields
    if (
      name === "address" ||
      name === "city" ||
      name === "zipCode" ||
      name === "ownerStatus" ||
      name === "householdIncome" ||
      name === "householdSize" ||
      name === "evs"
    ) {
      const message = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: message }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true,
    }));
    if (
      name === "address" ||
      name === "city" ||
      name === "zipCode" ||
      name === "ownerStatus" ||
      name === "householdIncome" ||
      name === "householdSize" ||
      name === "customerSplit" ||
      name === "softwareProviderSplit" ||
      name === "operatorSplit"
    ) {
      const message = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: message }));
    }
  };

  const validateField = (name: string, value: string) => {
    if (
      (name === "address" ||
        name === "city" ||
        name === "zipCode" ||
        name === "ownerStatus" ||
        name === "householdSize" ||
        name === "evs") &&
      !value.trim()
    ) {
      return "This field is required";
    }
    if (name === "zipCode") {
      if (value && !/^\d+$/.test(value)) {
        return "Zip Code can only contain numbers (0-9)";
      }
      if (value && value.length !== 5) {
        return "Zip Code must be exactly 5 digits";
      }
    }
    if (name === "householdIncome" && value) {
      if (!/^\d*\.?\d*$/.test(value)) {
        return "Household Income must be a valid number";
      }
      if (parseFloat(value) < 0) {
        return "Household Income cannot be negative";
      }
    }
    // Tax benefits validation
    if (name === "businessTaxRate" && value) {
      const rate = parseFloat(value);
      if (isNaN(rate) || rate < 0 || rate > 100) {
        return "Business tax rate must be between 0 and 100";
      }
    }
    if (name === "section179Limit" && value) {
      const limit = parseFloat(value);
      if (isNaN(limit) || limit < 0) {
        return "Section 179 limit must be a positive number";
      }
    }
    if (name === "bonusDepreciationPct" && value) {
      const pct = parseFloat(value);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        return "Bonus depreciation must be between 0 and 100";
      }
    }

    // Profit sharing validation
    if (
      name === "customerSplit" ||
      name === "softwareProviderSplit" ||
      name === "operatorSplit"
    ) {
      if (value && !/^\d*\.?\d*$/.test(value)) {
        return "Must be a valid number";
      }
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0 || numValue > 100) {
        return "Must be between 0 and 100";
      }

      // Check total sum
      const customer = parseFloat(formData.customerSplit || "0") || 0;
      const software = parseFloat(formData.softwareProviderSplit || "0") || 0;
      const operator = parseFloat(formData.operatorSplit || "0") || 0;
      const currentFieldValue = numValue;

      let total = customer + software + operator;

      // If we are updating a field, subtract its old value and add the new one
      if (name === "customerSplit") {
        total =
          total -
          (parseFloat(formData.customerSplit || "0") || 0) +
          currentFieldValue;
      } else if (name === "softwareProviderSplit") {
        total =
          total -
          (parseFloat(formData.softwareProviderSplit || "0") || 0) +
          currentFieldValue;
      } else if (name === "operatorSplit") {
        total =
          total -
          (parseFloat(formData.operatorSplit || "0") || 0) +
          currentFieldValue;
      }

      if (total > 100) {
        return `Total cannot exceed 100 percent (currently ${total.toFixed(
          1
        )} percent)`;
      }
    }
    return "";
  };

  const validateAll = () => {
    const newErrors: Record<string, string> = {};
    newErrors.address = validateField("address", formData.address ?? "");
    newErrors.city = validateField("city", formData.city ?? "");
    newErrors.zipCode = validateField("zipCode", formData.zipCode ?? "");

    // Profit sharing validation
    newErrors.customerSplit = validateField(
      "customerSplit",
      formData.customerSplit ?? ""
    );
    newErrors.softwareProviderSplit = validateField(
      "softwareProviderSplit",
      formData.softwareProviderSplit ?? ""
    );
    newErrors.operatorSplit = validateField(
      "operatorSplit",
      formData.operatorSplit ?? ""
    );

    // Check total sum
    const customer = parseFloat(formData.customerSplit || "0") || 0;
    const software = parseFloat(formData.softwareProviderSplit || "0") || 0;
    const operator = parseFloat(formData.operatorSplit || "0") || 0;
    const total = customer + software + operator;

    if (total > 100) {
      const errorMsg = `Profit sharing cannot exceed 100 percent (currently ${total.toFixed(
        1
      )} percent)`;
      if (!newErrors.customerSplit) newErrors.customerSplit = errorMsg;
      if (!newErrors.softwareProviderSplit)
        newErrors.softwareProviderSplit = errorMsg;
      if (!newErrors.operatorSplit) newErrors.operatorSplit = errorMsg;
    } else if (total !== 100) {
      const errorMsg = `Profit sharing must total exactly 100 percent (currently ${total.toFixed(
        1
      )} percent)`;
      if (!newErrors.customerSplit) newErrors.customerSplit = errorMsg;
      if (!newErrors.softwareProviderSplit)
        newErrors.softwareProviderSplit = errorMsg;
      if (!newErrors.operatorSplit) newErrors.operatorSplit = errorMsg;
    }

    // Only validate incentives fields if not excluded
    if (!excludeIncentives) {
      newErrors.ownerStatus = validateField(
        "ownerStatus",
        formData.ownerStatus ?? ""
      );
      newErrors.householdIncome = validateField(
        "householdIncome",
        formData.householdIncome ?? ""
      );
      newErrors.householdSize = validateField(
        "householdSize",
        formData.householdSize ?? ""
      );
    }

    if (
      formData.zipCode &&
      (!/^\d+$/.test(formData.zipCode) || formData.zipCode.length !== 5)
    ) {
      newErrors.zipCode =
        formData.zipCode.length !== 5
          ? "Zip Code must be exactly 5 digits"
          : "Zip Code can only contain numbers (0-9)";
    }

    Object.keys(newErrors).forEach(k => {
      if (!newErrors[k]) delete newErrors[k];
    });
    setErrors(prev => ({ ...prev, ...newErrors }));
    setTouched(prev => ({
      ...prev,
      address: true,
      city: true,
      zipCode: true,
      ownerStatus: true,
      householdIncome: true,
      householdSize: true,
      customerSplit: true,
      softwareProviderSplit: true,
      operatorSplit: true,
    }));

    const hasErrors = Object.keys(newErrors).length > 0;
    const isProfitSharingInvalid = total > 100 || total < 100;

    return !hasErrors && !isProfitSharingInvalid;
  };

  const isProfitSharingValid = () => {
    const customer = parseFloat(formData.customerSplit || "0") || 0;
    const software = parseFloat(formData.softwareProviderSplit || "0") || 0;
    const operator = parseFloat(formData.operatorSplit || "0") || 0;
    const total = customer + software + operator;

    return total === 100;
  };

  const handleMinChargingOutputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.includes(" ")) {
      toast.error("Minimum Charging Output cannot contain spaces");
      return;
    }
    if (parseFloat(e.target.value) < 0) {
      toast.error("Minimum charging output cannot be negative");
      return;
    }
    setMinChargingOutput(e.target.value);
  };

  const handleMaxChargingOutputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.includes(" ")) {
      toast.error("Maximum Charging Output cannot contain spaces");
      return;
    }
    if (parseFloat(e.target.value) < 0) {
      toast.error("Maximum charging output cannot be negative");
      return;
    }
    setMaxChargingOutput(e.target.value);
  };

  const handleHardwarePerInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.includes(" ")) {
      toast.error("Hardware Cost cannot contain spaces");
      return;
    }
    if (parseFloat(e.target.value) < 0) {
      toast.error("Hardware cost cannot be negative");
      return;
    }
    setHardwarePerInput(e.target.value);
  };

  const handleInstallPerInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.includes(" ")) {
      toast.error("Install Cost cannot contain spaces");
      return;
    }
    if (parseFloat(e.target.value) < 0) {
      toast.error("Install cost cannot be negative");
      return;
    }
    setInstallPerInput(e.target.value);
  };

  const handleAddChargingOutput = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const min = parseFloat(minChargingOutput);
    const max = parseFloat(maxChargingOutput);
    const hardwarePer = parseFloat(hardwarePerInput);
    const installPer = parseFloat(installPerInput);
    if (
      !isNaN(min) &&
      !isNaN(max) &&
      min <= max &&
      !isNaN(hardwarePer) &&
      hardwarePer > 0 &&
      !isNaN(installPer) &&
      installPer > 0
    ) {
      const result = await dispatch(
        createUserChargingOutput({
          minkW: min,
          maxkW: max,
          hardwareCost: Math.round(hardwarePer),
          installCost: Math.round(installPer),
        })
      );
      if (createUserChargingOutput.fulfilled.match(result)) {
        dispatch(getUserSpecificChargingOutput({ limit: 100 }));
        setMinChargingOutput("");
        setMaxChargingOutput("");
        setHardwarePerInput("");
        setInstallPerInput("");
        setFormData(prev => ({ ...prev, type: prev.type }));
      }
    } else {
      toast.error("Please enter valid values (min ≤ max, costs > 0).");
    }
  };

  const calculateEnergyCosts = (
    scheduleDetailData: any | null,
    reportDataParam: ReportData | null,
    baseRate: number
  ) => {
    const zeros = Array(5).fill(0);
    if (!reportDataParam) {
      return {
        energy: zeros,
        energyTime: zeros,
        incrementalEnergy: zeros,
        demand: zeros,
        demandTime: zeros,
        incrementalDemand: zeros,
        serviceCharge: zeros,
        otherCharges: zeros,
        total: zeros,
      };
    }

    const kWh = reportDataParam.kWhYearArr || zeros;

    // Helpers
    const toNum = (v: any) => {
      const n = parseFloat(
        String(v ?? "")
          .toString()
          .replace(/[^0-9.-]/g, "")
      );
      return Number.isFinite(n) ? n : 0;
    };

    // 1) ENERGY: simple baseline using fetched rate
    const energy = kWh.map(x => x * (baseRate || 0));

    // 2) DEMAND: conservative monthly generic row only, 25 percent concurrency for L2
    let monthlyDemandRate = 0;
    const Demand_Table = scheduleDetailData?.Demand_Table || [];
    const toNumStrict = (v: any) => {
      const s = String(v ?? "").trim();
      if (/[a-zA-Z]/.test(s)) return NaN;
      const n = parseFloat(s.replace(/[^0-9.-]/g, ""));
      return Number.isFinite(n) ? n : NaN;
    };

    for (const d of Demand_Table) {
      const unit = String(d.ChargeUnit || "").toLowerCase();
      const determinant = String(d.Determinant || "").toLowerCase();
      const rate = toNumStrict(d.RatekW);
      const looksMonthlyKW =
        unit.includes("month") && determinant.includes("kw");
      const generic = !/4cp|tou|on[-\s]?peak|off[-\s]?peak|summer|winter/.test(
        determinant
      );
      if (looksMonthlyKW && generic && rate > 0) {
        monthlyDemandRate = Math.max(monthlyDemandRate, rate);
      }
    }

    const chargers = toNum(formData.chargers) || 1;
    const whpm = (presets[formData.type] || { whpm: 7.2 }).whpm;
    const concurrency = 0.25;
    const billableKW = Math.max(30, chargers * whpm * concurrency);

    const demandAnnual = monthlyDemandRate * billableKW * 12;
    const demand = Array(5).fill(demandAnnual);

    // 3) SERVICE CHARGE: sum dollar per month rows, annualize
    let monthlyService = 0;
    const ServiceCharge_Table = scheduleDetailData?.ServiceCharge_Table || [];
    for (const sc of ServiceCharge_Table) {
      const unit = String(sc.ChargeUnit || "").toLowerCase();
      const rate = toNum(sc.Rate);
      if (unit.includes("month") && rate > 0) monthlyService += rate;
    }
    const serviceAnnual = monthlyService * 12;
    const serviceCharge = Array(5).fill(serviceAnnual);

    // 4) OTHER CHARGES: conservative (ignore unless clearly dollar per month)
    let monthlyOther = 0;
    const OtherCharges_Table = scheduleDetailData?.OtherCharges_Table || [];
    for (const oc of OtherCharges_Table) {
      const unit = String(oc.ChargeUnit || "").toLowerCase();
      const rate = toNumStrict(oc.Rate);
      if (unit.includes("month") && Number.isFinite(rate) && rate > 0) {
        monthlyOther += rate;
      }
    }

    const otherAnnual = monthlyOther * 12;
    const otherCharges = Array(5).fill(otherAnnual);

    const energyTime = zeros;
    const incrementalEnergy = zeros;
    const demandTime = zeros;
    const incrementalDemand = zeros;

    const total = energy.map(
      (e, i) => e + demand[i] + serviceCharge[i] + otherCharges[i]
    );

    return {
      energy,
      energyTime,
      incrementalEnergy,
      demand,
      demandTime,
      incrementalDemand,
      serviceCharge,
      otherCharges,
      total,
    };
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const isValid = validateAll();
    if (!isValid) {
      toast.error("Please fill in the required fields correctly.");
      return;
    }

    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const updatedReport = calculateReport(
        formData,
        0.15,
        utilityRate,
        presets
      );

      const grantCostDetails = excludeIncentives
        ? []
        : processGrantCostDetails(incentivesData || undefined);

      const formDataToSave = excludeIncentives
        ? {
            ...formData,
            ownerStatus: "",
            householdIncome: "",
            householdSize: "",
            grant: "0",
          }
        : formData;

      const upfrontCost = updatedReport.applyTaxBenefits
        ? updatedReport.upfrontAfterTax || 0
        : updatedReport.install + updatedReport.hardware;

      const cashFlowData = [-upfrontCost];

      for (let i = 0; i < 5; i++) {
        const yVal =
          updatedReport.chargingRevArr[i] +
          updatedReport.indirectArr[i] -
          updatedReport.maintenance / 5 -
          updatedReport.kWhYearArr[i] * utilityRate;
        cashFlowData.push(cashFlowData[i] + yVal);
      }

      const zipCode = formData.zipCode;
      const energyCosts = calculateEnergyCosts(
        scheduleDetailData,
        updatedReport,
        utilityRate
      );
      updatedReport.utilityCost5 = energyCosts.total.reduce(
        (sum, x) => sum + x,
        0
      );

      const totalUtilityCost = energyCosts.total.reduce(
        (sum, x) => sum + x,
        0
      );

      const utilityDetails = {
        state: utilityData?.State || selectedSchedule?.State || "--",
        zipCode,
        utilityName:
          utilityData?.UtilityName || selectedSchedule?.UtilityName || "--",
        utilityId:
          utilityData?.UtilityID || selectedSchedule?.UtilityID || "--",
        scheduleId: selectedSchedule?.ScheduleID || "--",
        totalCost: totalUtilityCost.toFixed(2),
      };

      const customerSplit = parseFloat(formData.customerSplit || "100") || 100;
      const softwareProviderSplit =
        parseFloat(formData.softwareProviderSplit || "0") || 0;
      const operatorSplit =
        parseFloat(formData.operatorSplit || "0") || 0;

      const netProfitBeforeSharing = updatedReport.netProfit5;
      const customerProfit = Math.round(
        (customerSplit / 100) * netProfitBeforeSharing
      );
      const softwareProviderProfit = Math.round(
        (softwareProviderSplit / 100) * netProfitBeforeSharing
      );
      const operatorProfit = Math.round(
        (operatorSplit / 100) * netProfitBeforeSharing
      );

      const savedReport = {
        reportName: `${formData.address} Charging Station`,
        location: formData.address,
        zipCode: formData.zipCode,
        netProfit: updatedReport.netProfit5,
        financialTable: [
          {
            category: "Charger Revenue (5yrs)",
            value: updatedReport.chargingRev5,
          },
          {
            category: "Added Dwell Time (per visit)",
            value: updatedReport.addedDwell,
          },
          {
            category: "Additional Spend (per visit)",
            value: updatedReport.addSpend,
          },
          { category: "Traffic Increase", value: updatedReport.traffic },
          {
            category: "Indirect Revenue (5yrs)",
            value: updatedReport.indirectRev5,
          },
          { category: "Total Revenue", value: updatedReport.totalRev5 },
          { category: "Install Cost", value: updatedReport.install },
          { category: "Hardware Cost", value: updatedReport.hardware },
          { category: "Network Cost", value: updatedReport.network },
          { category: "Maintenance", value: updatedReport.maintenance },
          {
            category: "Utility Bill (Energy + Demand + Fixed)",
            value: energyCosts.total.reduce((sum, x) => sum + x, 0).toFixed(0),
          },
          {
            category: "Grants/Subsidies & Tax Credit",
            value: updatedReport.grant,
          },
          ...(updatedReport.applyTaxBenefits
            ? [
                {
                  category: "Section 179 / 168(k) Tax Savings (Est.)",
                  value: Math.round(updatedReport.taxSavings || 0),
                },
              ]
            : []),
          {
            category: `Customer Share (${customerSplit} percent)`,
            value: customerProfit,
          },
          {
            category: `Software Provider Share (${softwareProviderSplit} percent)`,
            value: softwareProviderProfit,
          },
          {
            category: `Operator Share (${operatorSplit} percent)`,
            value: operatorProfit,
          },
          { category: "Net Profit", value: updatedReport.netProfit5 },
        ],
        breakEvenCashFlow: {
          labels: ["0", "1", "2", "3", "4", "5"],
          data: cashFlowData,
        },
        revenueCost: {
          labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
          datasets: [
            { label: "Charging Rev", data: updatedReport.chargingRevArr },
            { label: "Indirect Rev", data: updatedReport.indirectArr },
            {
              label: "Install Cost",
              data: [updatedReport.install, 0, 0, 0, 0],
            },
            {
              label: "Hardware Cost",
              data: [updatedReport.hardware, 0, 0, 0, 0],
            },
            {
              label: "Network Cost",
              data: Array(5).fill(updatedReport.network / 5),
            },
            {
              label: "Maintenance",
              data: Array(5).fill(updatedReport.maintenance / 5),
            },
            {
              label: "Utility Cost",
              data: updatedReport.kWhYearArr.map(k => k * utilityRate),
            },
          ],
        },
        co2Savings: {
          labels: ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5"],
          datasets: [
            {
              label: "CO₂ Offset (kg)",
              data: updatedReport.co2Arr.map(x => Math.round(x)),
            },
            {
              label: "Gasoline Replaced (gal)",
              data: updatedReport.gasArr.map(x => Math.round(x)),
            },
          ],
        },
        utilityDetails,
        energyCosts,
        grantCostDetails,
        excludeIncentives,
        disableUtilityEnergyInfo: formData.disableUtilityEnergyInfo,

        applyTaxBenefits: updatedReport.applyTaxBenefits,
        businessTaxRate: updatedReport.businessTaxRate,
        section179Limit: updatedReport.section179Limit,
        bonusDepreciationPct: updatedReport.bonusDepreciationPct,
        qualifyingSpend: updatedReport.qualifyingSpend,
        taxSavings: updatedReport.taxSavings,
        upfrontAfterTax: updatedReport.upfrontAfterTax,

        utilization: 0.15,
        utilityRate,
        timestamp: new Date().toISOString(),
      };

      const result = await dispatch(savedGeneratedReport(savedReport)).unwrap();

      const createdReportId = result.data?.report?._id;

      const savedReportWithId = {
        ...savedReport,
        _id: createdReportId,
      };

      navigate("/main/generate-reports/view", {
        state: {
          formData: formDataToSave,
          reportData: updatedReport,
          savedReport: savedReportWithId,
          utilization: 0.15,
          utilityRate,
          utilityDetails,
          energyCosts,
          grantCostDetails,
          excludeIncentives,
          disableUtilityEnergyInfo: formData.disableUtilityEnergyInfo,

          applyTaxBenefits: updatedReport.applyTaxBenefits,
          businessTaxRate: updatedReport.businessTaxRate,
          section179Limit: updatedReport.section179Limit,
          bonusDepreciationPct: updatedReport.bonusDepreciationPct,
          qualifyingSpend: updatedReport.qualifyingSpend,
          taxSavings: updatedReport.taxSavings,
          upfrontAfterTax: updatedReport.upfrontAfterTax,

          customerSplit: formData.customerSplit,
          softwareProviderSplit: formData.softwareProviderSplit,
          operatorSplit: formData.operatorSplit,
          customerProfit,
          softwareProviderProfit,
          operatorProfit,
        },
      });
    } catch (error) {
      console.error("Error generating or saving report.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (
        e.target instanceof HTMLInputElement &&
        (e.target.name === "minChargingOutput" ||
          e.target.name === "maxChargingOutput" ||
          e.target.name === "hardwarePerInput" ||
          e.target.name === "installPerInput")
      ) {
        handleAddChargingOutput();
      } else {
        handleSubmit(e as any);
      }
    }
  };

  const renderPricingInputs = () => {
    switch (formData.pricingType) {
      case "kwh":
        return (
          <Grid size={{ xs: 12, sm: 4 }}>
            <FieldLabel>Price per kWh</FieldLabel>
            <StyledTextField
              name="pricePerKwh"
              variant="outlined"
              type="number"
              value={formData.pricePerKwh}
              onChange={handleChange}
              onBlur={handleBlur}
              size={isMobile ? "small" : "medium"}
              inputProps={{ min: "0", step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        );
      case "minute":
        return (
          <Grid size={{ xs: 12, sm: 4 }}>
            <FieldLabel>Price per minute (¢/min)</FieldLabel>
            <StyledTextField
              name="pricePerMin"
              variant="outlined"
              type="number"
              value={formData.pricePerMin}
              onChange={handleChange}
              onBlur={handleBlur}
              size={isMobile ? "small" : "medium"}
              inputProps={{ min: "0", step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        );
      case "hour":
        return (
          <Grid size={{ xs: 12, sm: 4 }}>
            <FieldLabel>Price per hour ($/hr)</FieldLabel>
            <StyledTextField
              name="pricePerHour"
              variant="outlined"
              type="number"
              value={formData.pricePerHour}
              onChange={handleChange}
              onBlur={handleBlur}
              size={isMobile ? "small" : "medium"}
              inputProps={{ min: "0", step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        );
      case "session":
        return (
          <Grid size={{ xs: 12, sm: 4 }}>
            <FieldLabel>Flat session fee ($)</FieldLabel>
            <StyledTextField
              name="flatSessionFee"
              variant="outlined"
              type="number"
              value={formData.flatSessionFee}
              onChange={handleChange}
              onBlur={handleBlur}
              size={isMobile ? "small" : "medium"}
              inputProps={{ min: "0", step: "0.01" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        );
      case "minute_idle":
        return (
          <>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel>Price per minute (¢/min)</FieldLabel>
              <StyledTextField
                name="pricePerMin"
                variant="outlined"
                type="number"
                value={formData.pricePerMin}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel>Idle/Overstay Fee ($)</FieldLabel>
              <StyledTextField
                name="idleFee"
                variant="outlined"
                type="number"
                value={formData.idleFee}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel>Grace Period (minutes)</FieldLabel>
              <StyledTextField
                name="gracePeriod"
                variant="outlined"
                type="number"
                value={formData.gracePeriod}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.1" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </>
        );
      case "kwh_idle":
        return (
          <>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel>Price per kWh</FieldLabel>
              <StyledTextField
                name="pricePerKwh"
                variant="outlined"
                type="number"
                value={formData.pricePerKwh}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel>Idle/Overstay Fee ($)</FieldLabel>
              <StyledTextField
                name="idleFee"
                variant="outlined"
                type="number"
                value={formData.idleFee}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel>Grace Period (minutes)</FieldLabel>
              <StyledTextField
                name="gracePeriod"
                variant="outlined"
                type="number"
                value={formData.gracePeriod}
                onChange={handleChange}
                onBlur={handleBlur}
                size={isMobile ? "small" : "medium"}
                inputProps={{ min: "0", step: "0.1" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </>
        );
      case "free":
        return (
          <Grid size={{ xs: 12, sm: 4 }}>
            <FreeChargingText>
              Charging is free for the driver.
            </FreeChargingText>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <FormSection>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Content
            component="form"
            onSubmit={handleSubmit}
            onKeyDown={handleKeyPress}
          >
            <WelcomeText>
              Generate Report L2 Charging Station Estimator
            </WelcomeText>
            <Box
              sx={{
                background: "linear-gradient(135deg, #edefee 0 percent, #fcfbf9 100 percent)",
                padding: 2.5,
                mt: 2.5,
                borderRadius: 2,
              }}
            >
              <SubHeading>Location Details</SubHeading>
              <Grid container spacing={2} sx={{ width: "100 percent", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Address*</FieldLabel>
                  <StyledTextField
                    id="address-input"
                    name="address"
                    variant="outlined"
                    type="text"
                    value={formData.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.address && errors.address)}
                    helperText={
                      touched.address && errors.address ? errors.address : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>City*</FieldLabel>
                  <StyledTextField
                    name="city"
                    variant="outlined"
                    type="text"
                    value={formData.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.city && errors.city)}
                    helperText={touched.city && errors.city ? errors.city : ""}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Zip Code*</FieldLabel>
                  <StyledTextField
                    id="zipcode-input"
                    name="zipCode"
                    variant="outlined"
                    type="text"
                    value={formData.zipCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.zipCode && errors.zipCode)}
                    helperText={
                      touched.zipCode && errors.zipCode
                        ? errors.zipCode
                        : utilityLoading ||
                          scheduleLoading ||
                          energyLoading ||
                          scheduleDetailLoading
                        ? "Fetching ratekWh ..."
                        : ratekWh
                        ? `Utility Rate: ${parseFloat(ratekWh).toFixed(
                            4
                          )} $/kWh`
                        : utilityError ||
                          scheduleError ||
                          energyError ||
                          scheduleDetailError
                        ? "Failed to fetch ratekWh or data not available"
                        : "Enter 5 digit zip code (numbers only)"
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{
                      maxLength: 5,
                      inputMode: "numeric",
                      pattern: "[0-9]{5}",
                      placeholder: "12345",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon />
                        </InputAdornment>
                      ),
                      endAdornment:
                        utilityLoading ||
                        scheduleLoading ||
                        energyLoading ||
                        scheduleDetailLoading ? (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ) : null,
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ width: "100 percent", mb: 2 }}>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      userSelect: "none",
                      justifyContent: "flex-start",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="exclude-incentives"
                      checked={excludeIncentives}
                      onChange={e => setExcludeIncentives(e.target.checked)}
                      style={{
                        marginRight: "5px",
                        width: "16px",
                        height: "16px",
                        color: "#4b5b5d",
                      }}
                    />
                    <label
                      htmlFor="exclude-incentives"
                      style={{
                        fontSize: "0.9rem",
                        color: "#455354",
                        fontWeight: 500,
                      }}
                    >
                      Commercial Installation
                    </label>
                  </Box>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ width: "100 percent", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Owner Status*</FieldLabel>
                  <StyledTextField
                    select
                    name="ownerStatus"
                    variant="outlined"
                    value={formData.ownerStatus}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required={!excludeIncentives}
                    error={Boolean(touched.ownerStatus && errors.ownerStatus)}
                    helperText={
                      touched.ownerStatus && errors.ownerStatus
                        ? errors.ownerStatus
                        : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    disabled={excludeIncentives}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HomeIcon />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="homeowner">Homeowner</MenuItem>
                    <MenuItem value="renter">Renter</MenuItem>
                  </StyledTextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Household Income ($)*</FieldLabel>
                  <StyledTextField
                    name="householdIncome"
                    variant="outlined"
                    type="number"
                    value={formData.householdIncome}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required={!excludeIncentives}
                    error={Boolean(
                      touched.householdIncome && errors.householdIncome
                    )}
                    helperText={
                      touched.householdIncome && errors.householdIncome
                        ? errors.householdIncome
                        : "Enter household income"
                    }
                    size={isMobile ? "small" : "medium"}
                    disabled={excludeIncentives}
                    inputProps={{ min: "0", step: "1" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                      endAdornment: incentivesLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Household Size*</FieldLabel>
                  <StyledTextField
                    select
                    name="householdSize"
                    variant="outlined"
                    value={formData.householdSize}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required={!excludeIncentives}
                    error={Boolean(
                      touched.householdSize && errors.householdSize
                    )}
                    helperText={
                      touched.householdSize && errors.householdSize
                        ? errors.householdSize
                        : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    disabled={excludeIncentives}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <GroupIcon />
                        </InputAdornment>
                      ),
                    }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(size => (
                      <MenuItem key={size} value={size.toString()}>
                        {size} Person{size > 1 ? "s" : ""}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                </Grid>
              </Grid>
              <SubHeading>Charging Station Details</SubHeading>
              <Grid container spacing={2} sx={{ width: "100 percent", mb: 1 }}>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      userSelect: "none",
                      justifyContent: "flex-start",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="disableUtilityEnergyInfo"
                      name="disableUtilityEnergyInfo"
                      checked={formData.disableUtilityEnergyInfo || false}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          disableUtilityEnergyInfo: e.target.checked,
                        }))
                      }
                      style={{
                        marginRight: "5px",
                        width: "16px",
                        height: "16px",
                        color: "#4b5b5d",
                      }}
                    />
                    <label
                      htmlFor="disableUtilityEnergyInfo"
                      style={{
                        fontSize: "0.9rem",
                        color: "#455354",
                        fontWeight: 500,
                      }}
                    >
                      Exclude Demand Charges
                    </label>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#666", ml: 3, display: "block" }}
                  >
                    Check this box if your site is already subject to demand
                    charges, is near the threshold for incurring them, or has a
                    negotiated rate structure that avoids them. Selecting this
                    will remove demand charges from the revenue estimate.
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Average Parking Time*</FieldLabel>
                  <StyledTextField
                    select
                    name="dwell"
                    variant="outlined"
                    value={formData.dwell}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem
                      value="60"
                      data-category="Shopping Centers / Retail"
                    >
                      Shopping Centers / Retail (60m)
                    </MenuItem>
                    <MenuItem
                      value="0"
                      data-category="Apartment Complex / Multi-family"
                    >
                      Apartment Complex / Multi-family (14-18h)
                    </MenuItem>
                    <MenuItem value="600" data-category="Hotels / Lodging">
                      Hotels / Lodging (8-12h)
                    </MenuItem>
                    <MenuItem value="480" data-category="Office Buildings">
                      Office Buildings (8h)
                    </MenuItem>
                    <MenuItem value="090" data-category="Restaurants / Cafés">
                      Restaurants / Cafés (1-2h)
                    </MenuItem>
                    <MenuItem
                      value="150"
                      data-category="Theaters / Entertainment"
                    >
                      Theaters / Entertainment (2-3h)
                    </MenuItem>
                    <MenuItem value="90" data-category="Gyms / Fitness">
                      Gyms / Fitness (1-2h)
                    </MenuItem>
                    <MenuItem value="00" data-category="Hospitals / Medical">
                      Hospitals / Medical (2-8h)
                    </MenuItem>
                    <MenuItem value="000" data-category="Universities / Campus">
                      Universities / Campus (4-8h)
                    </MenuItem>
                    <MenuItem
                      value="1380"
                      data-category="Parking Garages / Lots"
                    >
                      Parking Garages / Lots (23h)
                    </MenuItem>
                    <MenuItem value="720" data-category="Household / Home">
                      Household / Home (8-12h)
                    </MenuItem>
                    <MenuItem
                      value="45"
                      data-category="Grocery Stores / Supermarkets"
                    >
                      Grocery Stores / Supermarkets (≈45m)
                    </MenuItem>
                  </StyledTextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Chargers*</FieldLabel>
                  <StyledTextField
                    name="chargers"
                    variant="outlined"
                    type="number"
                    value={formData.chargers}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BoltIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>EV Drivers Volume*</FieldLabel>
                  <StyledTextField
                    select
                    name="evs"
                    variant="outlined"
                    value={formData.evs}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </StyledTextField>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ width: "100 percent", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Charging Output*</FieldLabel>
                  <StyledTextField
                    select
                    name="type"
                    variant="outlined"
                    value={formData.type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BatteryChargingFullIcon />
                        </InputAdornment>
                      ),
                    }}
                    SelectProps={{
                      MenuProps: {
                        ...CustomMenuProps,
                        disableAutoFocusItem: true,
                      },
                      renderValue: value => chargingOutput[value as string],
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        borderBottom: "1px solid #e0e0e0",
                        backgroundColor: "#f8f9fa",
                      }}
                      onClick={e => e.stopPropagation()}
                      onKeyDown={e => e.stopPropagation()}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "#4b5b5d",
                          fontWeight: 600,
                          mb: 1,
                          textAlign: "center",
                          fontSize: "0.875rem",
                        }}
                      >
                        Add Custom Charging Output
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                        <CustomInput
                          name="minChargingOutput"
                          label="Min kW"
                          variant="outlined"
                          type="number"
                          inputProps={{ step: "0.1", min: "0" }}
                          value={minChargingOutput}
                          onChange={handleMinChargingOutputChange}
                          onBlur={handleBlur}
                          size="small"
                          sx={{ flex: 1, margin: 0 }}
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => e.stopPropagation()}
                        />
                        <CustomInput
                          name="maxChargingOutput"
                          label="Max kW"
                          variant="outlined"
                          type="number"
                          inputProps={{ step: "0.1", min: "0" }}
                          value={maxChargingOutput}
                          onChange={handleMaxChargingOutputChange}
                          onBlur={handleBlur}
                          size="small"
                          sx={{ flex: 1, margin: 0 }}
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => e.stopPropagation()}
                        />
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                        <CustomInput
                          name="hardwarePerInput"
                          label="Hardware/charger ($)"
                          variant="outlined"
                          type="number"
                          inputProps={{ min: "0" }}
                          value={hardwarePerInput}
                          onChange={handleHardwarePerInputChange}
                          onBlur={handleBlur}
                          size="small"
                          sx={{ flex: 1, margin: 0 }}
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => e.stopPropagation()}
                        />
                        <CustomInput
                          name="installPerInput"
                          label="Install/charger ($)"
                          variant="outlined"
                          type="number"
                          inputProps={{ min: "0" }}
                          value={installPerInput}
                          onChange={handleInstallPerInputChange}
                          onBlur={handleBlur}
                          size="small"
                          sx={{ flex: 1, margin: 0 }}
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => e.stopPropagation()}
                        />
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <motion.div
                          whileHover={{
                            scale: 1.04,
                          }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <DropdownButton
                            variant="contained"
                            onClick={handleAddChargingOutput}
                            disabled={
                              !minChargingOutput ||
                              !maxChargingOutput ||
                              !hardwarePerInput ||
                              !installPerInput
                            }
                          >
                            Add Custom Output
                          </DropdownButton>
                        </motion.div>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        p: 1,
                        backgroundColor: "#f5f5f5",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#666",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      >
                        Available Charging Outputs
                      </Typography>
                    </Box>
                    {chargingOutputOrder.map(key => (
                      <MenuItem
                        key={key}
                        value={key}
                        sx={{
                          fontSize: "0.7rem",
                          padding: "5px 5px",
                          margin: "1px 5px",
                          borderRadius: "8px",
                          border: "1px solid transparent",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#F4F4F4",
                            borderColor: "#455354",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "#F4F4F4",
                            borderColor: "#455354",
                            color: "#70936D",
                            fontWeight: 600,
                          },
                        }}
                      >
                        {chargingOutput[key]}
                      </MenuItem>
                    ))}
                  </StyledTextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Hardware Cost (Total)*</FieldLabel>
                  <StyledTextField
                    name="hardware"
                    variant="outlined"
                    type="number"
                    value={formData.hardware}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "1" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Install Cost (Total)*</FieldLabel>
                  <StyledTextField
                    name="install"
                    variant="outlined"
                    type="number"
                    value={formData.install}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "1" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Driver Pricing</FieldLabel>
                  <StyledTextField
                    select
                    name="pricingType"
                    variant="outlined"
                    value={formData.pricingType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="kwh">Per kWh</MenuItem>
                    <MenuItem value="minute">Per Minute</MenuItem>
                    <MenuItem value="hour">Per Hour</MenuItem>
                    <MenuItem value="session">Flat Session Fee</MenuItem>
                    <MenuItem value="minute_idle">
                      Per Minute + Idle/Overstay Fee
                    </MenuItem>
                    <MenuItem value="kwh_idle">
                      Per kWh + Idle/Overstay Fee
                    </MenuItem>
                    <MenuItem value="free">Free Charging</MenuItem>
                  </StyledTextField>
                </Grid>
                {renderPricingInputs()}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Annual Maintenance (S/Charger)</FieldLabel>
                  <StyledTextField
                    name="maintenance"
                    variant="outlined"
                    type="number"
                    value={formData.maintenance}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BuildIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Network Cost Per Port</FieldLabel>
                  <StyledTextField
                    name="networkPerPort"
                    variant="outlined"
                    type="number"
                    value={formData.networkPerPort}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Additional Grant/Subsidy</FieldLabel>
                  <StyledTextField
                    name="grant"
                    variant="outlined"
                    type="number"
                    value={formData.grant}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <SubHeading>Tax Benefits (Section 179 and 168(k))</SubHeading>
              <Grid size={{ xs: 12 }}>
                <Grid container spacing={2} sx={{ width: "100 percent", mb: 2 }}>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <input
                        type="checkbox"
                        id="applyTaxBenefits"
                        name="applyTaxBenefits"
                        checked={formData.applyTaxBenefits || false}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            applyTaxBenefits: e.target.checked,
                          }))
                        }
                        style={{
                          width: "16px",
                          height: "16px",
                          color: "#4b5b5d",
                        }}
                      />
                      <FieldLabel>
                        Apply Section 179 and 168(k) Tax Benefits?
                      </FieldLabel>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", ml: 3, display: "block" }}
                    >
                      Uses 2025 United States limits for EV equipment
                      deductions. Consult your CPA.
                    </Typography>
                  </Grid>
                </Grid>

                {formData.applyTaxBenefits && (
                  <Grid container spacing={2} sx={{ width: "100 percent" }}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FieldLabel>Business Tax Rate (%)*</FieldLabel>
                      <StyledTextField
                        name="businessTaxRate"
                        variant="outlined"
                        type="number"
                        value={formData.businessTaxRate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: "0", max: "100", step: "0.1" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Approximate federal corporate rate; adjust if needed.
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FieldLabel>Section 179 Limit ($)*</FieldLabel>
                      <StyledTextField
                        name="section179Limit"
                        variant="outlined"
                        type="number"
                        value={formData.section179Limit}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: "0", step: "1000" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Default 2025 limit; phase out rules apply at higher
                        spend.
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FieldLabel>
                        Bonus Depreciation (168(k)) percent*
                      </FieldLabel>
                      <StyledTextField
                        name="bonusDepreciationPct"
                        variant="outlined"
                        type="number"
                        value={formData.bonusDepreciationPct}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: "0", max: "100", step: "1" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Percent of remaining qualifying spend above the Section
                        179 limit.
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                {formData.applyTaxBenefits && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1,
                      backgroundColor: "#fff3cd",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "#856404", fontStyle: "normal" }}
                    >
                      <strong>Tax disclaimer:</strong> Estimates are
                      illustrative only and do not constitute tax advice.
                      Section 179 and 168(k) eligibility and limits vary by
                      entity and state. Consult a qualified tax professional to
                      confirm treatment for your situation.
                    </Typography>
                  </Box>
                )}
              </Grid>

              <SubHeading>Profit Sharing</SubHeading>
              <Grid container spacing={2} sx={{ width: "100 percent", mb: 2 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Customer Split (%)*</FieldLabel>
                  <StyledTextField
                    name="customerSplit"
                    variant="outlined"
                    type="number"
                    value={formData.customerSplit || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", max: "100", step: "0.1" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Software Provider Split (%)*</FieldLabel>
                  <StyledTextField
                    name="softwareProviderSplit"
                    variant="outlined"
                    type="number"
                    value={formData.softwareProviderSplit || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", max: "100", step: "0.1" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Operator Split (%)*</FieldLabel>
                  <StyledTextField
                    name="operatorSplit"
                    variant="outlined"
                    type="number"
                    value={formData.operatorSplit || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", max: "100", step: "0.1" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              {(touched.customerSplit ||
                touched.softwareProviderSplit ||
                touched.operatorSplit) &&
                (errors.customerSplit ||
                  errors.softwareProviderSplit ||
                  errors.operatorSplit) && (
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#d32f2f", fontSize: "0.75rem" }}
                    >
                      {errors.customerSplit ||
                        errors.softwareProviderSplit ||
                        errors.operatorSplit}
                    </Typography>
                  </Box>
                )}

              <Box
                sx={{
                  width: "100 percent",
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 2,
                }}
              >
                <motion.div
                  whileHover={{
                    scale: 1.04,
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <PrimaryButton
                    type="submit"
                    variant="contained"
                    disabled={
                      loading ||
                      utilityLoading ||
                      scheduleLoading ||
                      energyLoading ||
                      scheduleDetailLoading ||
                      incentivesLoading ||
                      !isZipApiComplete ||
                      !isProfitSharingValid()
                    }
                    startIcon={
                      loading ||
                      utilityLoading ||
                      scheduleLoading ||
                      energyLoading ||
                      scheduleDetailLoading ||
                      incentivesLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    {loading ||
                    utilityLoading ||
                    scheduleLoading ||
                    energyLoading ||
                    scheduleDetailLoading ||
                    incentivesLoading
                      ? "Generating..."
                      : "Calculate"}
                  </PrimaryButton>
                </motion.div>
              </Box>
            </Box>
          </Content>
        </motion.div>
      </FormSection>
    </Container>
  );
};

export default GenerateReports;